import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type LearningPathsResponse,
  type LearningPathsParams,
  type LearningPathItem,
} from "../../services/reports-extended.api";
import { Projects, Approve, Recent } from "@/components/icons";

const PAGE_SIZE = 20;

const PathCard: React.FC<{ path: LearningPathItem }> = ({ path }) => {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h4
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            {path.startEducationTitle}
          </h4>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
            {path.stepCount} adım · {path.totalEnrollments} başlangıç kaydı ·
            ort. {path.avgDurationDays} gün
          </div>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 12px",
            borderRadius: 999,
            background:
              path.overallCompletionRate >= 70
                ? "#dcfce7"
                : path.overallCompletionRate >= 40
                  ? "#fef3c7"
                  : "#fee2e2",
            color:
              path.overallCompletionRate >= 70
                ? "#166534"
                : path.overallCompletionRate >= 40
                  ? "#92400e"
                  : "#b91c1c",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {path.overallCompletionRate.toFixed(1)}% uçtan uca
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          overflowX: "auto",
          gap: 0,
        }}
      >
        {path.steps.map((step, idx) => (
          <React.Fragment key={step.educationID}>
            <div
              style={{
                flex: "0 0 auto",
                padding: "10px 14px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                minWidth: 200,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  letterSpacing: "0.05em",
                  fontWeight: 600,
                }}
              >
                Adım {idx + 1}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0f172a",
                  lineHeight: "18px",
                }}
              >
                {step.title}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                  marginTop: 4,
                  fontSize: 11.5,
                  color: "#64748b",
                }}
              >
                <span>{step.enrolledCount} kayıt</span>
                <span style={{ fontWeight: 600, color: "#16a34a" }}>
                  %{step.completionRate.toFixed(0)}
                </span>
              </div>
              {idx > 0 && step.dropoffFromPrevious > 0 && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#b91c1c",
                    fontWeight: 600,
                  }}
                >
                  −{step.dropoffFromPrevious} düşüş
                </div>
              )}
            </div>
            {idx < path.steps.length - 1 && (
              <div
                style={{
                  alignSelf: "center",
                  padding: "0 10px",
                  color: "#cbd5e1",
                  fontSize: 22,
                  fontWeight: 700,
                }}
              >
                →
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const LearningPathsReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: LearningPathsParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: filter.search?.trim() || undefined,
    }),
    [filter.search, page],
  );

  const { data, isLoading, error } = useReportData<
    LearningPathsResponse,
    LearningPathsParams
  >({
    fetcher: (p, signal) => reportsExtendedApi.getLearningPaths(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig({
    reportKey: "LEARNING_PATHS",
    reportTitle: t("learningPaths.title"),
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("learningPaths.title")}
      layoutKey={`report:LEARNING_PATHS`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.strategic.title")}
      description={t("learningPaths.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="LEARNING_PATHS"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="egitim-yollari"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDateRange={false}
          showDepartment={false}
        />
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <ReportKPICard
          title="Toplam Yol"
          value={summary?.totalPaths ?? 0}
          icon={<Projects width={18} height={18} />}
        />
        <ReportKPICard
          title={t("learningPaths.avgStep")}
          value={summary ? summary.avgStepCount.toFixed(1) : "—"}
          icon={<Recent width={18} height={18} />}
        />
        <ReportKPICard
          title={t("learningPaths.endToEndAvg")}
          value={summary ? `${summary.overallCompletionRate.toFixed(1)}%` : "—"}
          icon={<Approve width={18} height={18} />}
          accent={
            summary && summary.overallCompletionRate >= 70
              ? "success"
              : summary && summary.overallCompletionRate < 40
                ? "danger"
                : "warning"
          }
        />
      </div>

      {isLoading && (
        <div
          style={{
            padding: 40,
            textAlign: "center",
            color: "#94a3b8",
            fontSize: 13,
          }}
        >
          Yükleniyor…
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div
          style={{
            padding: 40,
            textAlign: "center",
            color: "#94a3b8",
            fontSize: 13,
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
          }}
        >
          {error ? t("learningPaths.noData") : t("learningPaths.noPath")}
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {items.map((p) => (
            <PathCard key={p.pathID} path={p} />
          ))}
        </div>
      )}
    </ReportDetailLayout>
  );
};

export default LearningPathsReportPage;
